import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { activityDetailStore } from '@client/store/activityDetailStore';
import unitHelper from '@client/helper/unit';

export default function useFrameSelection({ xAxisKey, chartDataType, chartRef, tryToDrawChart } = {}) {
  const activityDetailStoreRef = activityDetailStore();

  const frameSelectionData = computed(() => {
    return activityDetailStoreRef.frameSelectingData || activityDetailStoreRef.frameSelectedData;
  });

  const gettingFrequency = computed(() => activityDetailStoreRef.gettingFrequency);
  const chartXAxisKey = computed(() => activityDetailStoreRef.chartXAxisKey);

  const internalActions = {
    // 框选进行中的事件
    frameSelecting(data) {
      activityDetailStoreRef.updateFrameSelectingData(data);
    },
    frameSelectCancel(data) {
      activityDetailStoreRef.updateFrameSelectingData(data);
    },
    // 框选结束的事件
    frameSelectingEnd(data) {
      activityDetailStoreRef.resetSelectedData(data, chartXAxisKey);
    }
  };

  // external action
  const resetSelect = () => {
    internalActions.frameSelectingEnd(null);
  };

  // 监听图表的框选鼠标事件
  const listenFrameSelection = (chartData) => {
    const checkValue = (val) => {
      if (val < 0) {
        return 0;
      }
      if (val > chartRef.value.contentWidth) {
        return chartRef.value.contentWidth;
      }

      return val;
    };

    const eventDown = (e) => {
      e.preventDefault();
      // e.stopPropagation()
      const clickTime = 150;
      const startTime = new Date().getTime();
      const isTouch = e.type.includes('touch');
      const moveEventType = isTouch ? 'touchmove' : 'mousemove';
      const upEventType = isTouch ? 'touchend' : 'mouseup';
      const downEvent = isTouch ? e.touches[0] : e;

      if (gettingFrequency.value) {
        return;
      }
      clearTimeout(frameSelectionTimeout.value);
      // TODO: todo_xjf 缓存 left
      const left = chartRef.value.el.getBoundingClientRect().left + chartRef.value.margin.left;
      let start = downEvent.clientX - left;
      let end = start;

      const mouseMove = (e) => {
        e.preventDefault();
        // e.stopPropagation()
        const moveEvent = isTouch ? e.touches[0] : e;
        end = moveEvent.clientX - left;

        internalActions.frameSelecting({
          start: chartRef.value.getValueFromXScale(checkValue(start <= end ? start : end)),
          end: chartRef.value.getValueFromXScale(checkValue(start <= end ? end : start))
        });
      };
      const mouseUp = (e) => {
        // e.preventDefault()
        // e.stopPropagation()
        // const upEvent = isTouch ? e.touches[0] : e
        document.body.removeEventListener(moveEventType, mouseMove);
        document.body.removeEventListener(upEventType, mouseUp);
        // end = upEvent.clientX - left

        // console.log(new Date().getTime() - startTime)

        if (Math.abs(start - end) < 3) {
          if (new Date().getTime() - startTime < clickTime) {
            // 时间短，判断为点击事件
            if (gettingFrequency.value) {
              return;
            }

            clearTimeout(frameSelectionTimeout.value);
            activityDetailStoreRef.lapSelectionChange([]);
            return;
          }
          start = (start + end) / 2 - 10;
          end = (start + end) / 2 + 10;
        }

        let startInfo = {
          index: 0,
          data: chartData[0]
        };
        let endInfo = {
          index: chartData.length - 1,
          data: chartData[chartData.length - 1]
        };

        if (chartData.length > 8) {
          const startValue = chartRef.value.getValueFromXScale(checkValue(start <= end ? start : end));
          const endValue = chartRef.value.getValueFromXScale(checkValue(start <= end ? end : start));

          startInfo = unitHelper.getNearlyDataByBinarySearch({
            originalData: chartData,
            key: xAxisKey,
            value: startValue
          });
          endInfo = unitHelper.getNearlyDataByBinarySearch({
            originalData: chartData,
            key: xAxisKey,
            value: endValue
          });

          if (endInfo.index - startInfo.index < 8) {
            if ((endInfo.index - startInfo.index) / 2 - 4 >= 0 && (endInfo.index - startInfo.index) / 2 + 4 <= chartData.length - 1) {
              startInfo = {
                index: (endInfo.index - startInfo.index) / 2 - 4,
                data: chartData[(endInfo.index - startInfo.index) / 2 - 4]
              };
              endInfo = {
                index: (endInfo.index - startInfo.index) / 2 + 4,
                data: chartData[(endInfo.index - startInfo.index) / 2 + 4]
              };
            } else if (startInfo.index + 8 <= chartData.length - 1) {
              endInfo = {
                index: startInfo.index + 8,
                data: chartData[startInfo.index + 8]
              };
            } else if (endInfo.index - 8 >= 0) {
              startInfo = {
                index: endInfo.index - 8,
                data: chartData[endInfo.index - 8]
              };
            }
          }
        }
        frameSelectionTimeout.value = setTimeout(() => {
          internalActions.frameSelectingEnd({
            chartDataType: chartDataType,
            start: startInfo,
            end: endInfo
          });
        }, 200);
      };

      document.body.addEventListener(moveEventType, mouseMove, {
        passive: false
      });
      document.body.addEventListener(upEventType, mouseUp, { passive: false });
    };

    chartRef.value.svg.on('mousedown', eventDown);
    chartRef.value.svg.on('touchstart', eventDown);
  };

  // 创建框选颜色块
  const createSelectionZone = () => {
    if (!chartRef.value) {
      return;
    }

    if (!frameSelectionData.value) {
      chartRef.value.deleteSelectionZone();
      return;
    }
    chartRef.value.createSelectionZone({
      start: frameSelectionData.value.start,
      end: frameSelectionData.value.end,
      color: 'rgba(24, 30, 48, 0.8)',
      edgeColor: '#00B3FF'
    });
  };

  watch(frameSelectionData, () => {
    if (!chartRef?.value) return;
    createSelectionZone();
  });

  const frameSelectionTimeout = ref(0);
  onBeforeUnmount(() => {
    if (frameSelectionTimeout.value) {
      clearTimeout(frameSelectionTimeout.value);
    }
  });

  // Dom 不会用到这个，只是给各 chart watch 使用
  // 是否展示整体预览图表
  const showOverview = computed(() => {
    return (
      activityDetailStoreRef.frameSelectedData &&
      'start' in activityDetailStoreRef.frameSelectedData &&
      'end' in activityDetailStoreRef.frameSelectedData
    );
  });

  if (typeof tryToDrawChart === 'function') watch(showOverview, tryToDrawChart);

  return {
    frameSelectionData,
    listenFrameSelection,
    createSelectionZone,
    resetSelect
  };
}
